import { useEffect, useState } from "react"
import { Socket } from "phoenix"
import CaseNormaliser from '@quiqupltd/quiqupjs/lib/services/case-normaliser'
import { createFeature } from '@quiqupltd/QuReactSDK/lib/components/qu-map/qu-map-abstract/qu-map-abstract.helpers'
import get from 'lodash/get'
import { useEnvVars } from "./useEnvVars"

type UseTrackingSocketProps = {
  token?: string
  // EXCORE MissionSocket.connect/3 requires BOTH tracking_token AND mission_id,
  // and the tracking:missions handler resolves the mission via
  // find_mission(mission_id) (there is no token->mission lookup). Without it the
  // socket connect is refused server-side with a FunctionClauseError, so the map
  // and courier never load. See ex-core-api mission_socket.ex.
  missionId?: string
  shouldConnect: boolean
}

const camelNormaliser = new CaseNormaliser(CaseNormaliser.camelCase)

const FEATURE_OPTIONS = {
  markerText: 'Your order',
  isTrackOrder: true,
}

export default function useTrackingSocket(props: UseTrackingSocketProps) {
  const { env } = useEnvVars()
  const [trackingSocket, setTrackingSocket] = useState<Socket | null>(null)
  const [liveOrder, setLiveOrder] = useState<any>({
    features: [],
  })
  const [mapCenter, setMapCenter] = useState<number[] | undefined>()
  const [invalidToken, setInvalidToken] = useState(false)

  useEffect(() => {
    if (
      !props.token ||
      !props.missionId ||
      !env.EXCORE_SOCKET_URL ||
      !props.shouldConnect
    )
      return

    const socket = new Socket(env.EXCORE_SOCKET_URL, {
      params: {
        tracking_token: props.token,
        mission_id: props.missionId,
      },
    })
    setTrackingSocket(socket)
    socket.connect()

    socket.onOpen(() => {
      const missionChannel = socket.channel(`tracking:missions:${props.token}`)
      missionChannel.join()

      missionChannel.on('update', async (updatedData: any) => {
        if (invalidToken) {
          setInvalidToken(false)
        }

        const data = await camelNormaliser.call(updatedData)
        if (data) {
          const { waypoint, numberOfWaypointsAhead } = data

          // waypoint is absent before a courier is assigned; guard before
          // mutating to avoid "Cannot set properties of undefined".
          // Courier/order fields in the payload are still handled below.
          let features: any[] = []
          if (waypoint) {
            waypoint.jobState = data.state
            features.push(createFeature(waypoint, 'WAYPOINT', FEATURE_OPTIONS))
          }

          const centerLat = parseFloat(get(data, 'region.centerLat'))
          const centerLon = parseFloat(get(data, 'region.centerLon'))
          const mapCenter =
            centerLat && centerLon ? [centerLat, centerLon] : undefined
          const availableCourier = !!get(data, 'courier')
          let quiqee: any

          if (availableCourier) {
            quiqee = {
              img: get(data, 'courier.avatar.thumb.url'),
              name: get(data, 'courier.firstname'),
              transportMode: get(data, 'courier.transportMode'),
              phone: get(data, 'courier.mobile'),
              jobState: get(data, 'state'),
            }

            if (get(data, 'courier.currentLocation.coords')) {
              features?.push({
                ...createFeature(
                  {
                    coords: get(data, 'courier.currentLocation.coords'),
                    fullname: get(data, 'courier.firstname'),
                    transportMode: get(data, 'courier.transportMode'),
                    jobState: get(data, 'state'),
                  },
                  'QUIQEE',
                  FEATURE_OPTIONS
                ),
              })
            }
          } else {
            quiqee = null
          }

          if (!availableCourier) {
            features = features?.filter(
              (feature) => feature.properties.type !== 'courier'
            )
          }

          setLiveOrder((prevLiveOrder: any) => ({
            ...prevLiveOrder,
            numberOfWaypointsAhead,
            jobState: data.state,
            orderId: data.slug,
            region: data.region,
            kind: data.kind,
            quiqee,
            features: [...features],
            waypoint,
          }))

          setMapCenter(mapCenter)
        }
      })

      missionChannel.on('error', (value) => {
        setInvalidToken(true)
      })
    })

    return () => {
      if (socket.isConnected()) {
        socket.disconnect()
      }
    }
  }, [
    props.token,
    props.missionId,
    env.EXCORE_SOCKET_URL,
    props.shouldConnect,
    invalidToken,
  ])

  return { liveOrder, mapCenter, invalidToken, trackingSocket }
}
